<template>
    <tr class="combinations-table-row">
      <td class="text-center">
        <SwitchInput
            @update:model-value="onUpdateIsActive"
            :model-value="combination.isActive"
        />
      </td>
        <td style="width: 40%">
            <MultiEntityInputComponent
                ref="tableMultiEntityInput"
                @update:model-value="onUpdateTables"
                :data-source="tablesDataSource"
                :model-value="combination.tables"
            />
        </td>
        <td>
            <NumberInput
                ref="preferredMinOccupancyInput"
                :options="{ min: 1 }"
                @update:model-value="onUpdatePreferredMinOccupancy"
                :model-value="combination.preferredMinimumOccupancy"
            />
        </td>
        <td>
            <NumberInput
                ref="maximumOccupancyInput"
                @update:model-value="onUpdateMaximumOccupancy"
                :options="{ min: 1 }"
                :model-value="combination.maximumOccupancy"
            />
        </td>
        <td>
            <NumberInput
                ref="sortOrderInput"
                @update:model-value="onUpdateSortOrder"
                :options="{ min: 0 }"
                :model-value="combination.sortOrder"
            />
        </td>
        <td>
            <Button
                @click="removeCombination(combination)"
                style="background-color: #fd397a; border-color: #fd397a"
                class="btn-danger"
                icon="la la-trash"
                aria-label="Cancel"
            />
        </td>
    </tr>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import type { IMultiEntityLinkDataSource } from "@/data/dao/service/BaseServiceDao";
    import { TableDao } from "@/data/dao/service/TableDao";
    import type { ITableCombinationToCreateOrUpdate } from "@/data/model/ITableCombination";
    import type { ITable } from "@/data/model/Table";
    import { ref } from "vue";

    import MultiEntityInputComponent from "@/components/elements/MultiEntityInputComponent.vue";
    import NumberInput from "@/portal/components/elements/NumberInput.vue";
    import Button from "primevue/button";
    import SwitchInput from "@/portal/components/elements/SwitchInput.vue";

    const props = defineProps<{
        combination: ITableCombinationToCreateOrUpdate;
    }>();

    const tables = ref<ITable[]>([]);
    const tableMultiEntityInput = ref<InstanceType<typeof MultiEntityInputComponent> | null>(null);
    const preferredMinOccupancyInput = ref<InstanceType<typeof NumberInput> | null>(null);
    const maximumOccupancyInput = ref<InstanceType<typeof NumberInput> | null>(null);
    const sortOrderInput = ref<InstanceType<typeof NumberInput> | null>(null);

    const emit = defineEmits(["onRemoveCombination"]);

    const tableDao = Factory.getInstance<TableDao>(TableDao);

    let tablesPromise: Promise<void> | null = null;

    function loadTables(): Promise<void> {
        if (!tablesPromise) {
            tablesPromise = getTables();
        }
        return tablesPromise;
    }

    function removeCombination(combinationToRemove: ITableCombinationToCreateOrUpdate) {
        emit("onRemoveCombination", combinationToRemove);
    }

    async function getTables() {
        const data = await tableDao.getData({ onlyActive: true, onlyWithSeats: true });
        tables.value = data.items;
    }

    const tablesDataSource: IMultiEntityLinkDataSource = {
        search: async (excludedIds, term) => {
            await loadTables();

            return tables.value.filter(
                (x) => !excludedIds.includes(x.id) && x.name.toLowerCase().includes(term?.toLowerCase() ?? "")
            );
        },
    };

    function onUpdateTables(tableIds: string[]) {
        props.combination.tables = tableIds;
    }

    function onUpdateSortOrder(value: number | undefined) {
        props.combination.sortOrder = value;
    }

    function onUpdateMaximumOccupancy(value: number | undefined) {
        props.combination.maximumOccupancy = value ?? 1;
    }

    function onUpdatePreferredMinOccupancy(value: number | undefined) {
        props.combination.preferredMinimumOccupancy = value ?? 1;
    }

    function onUpdateIsActive(value: boolean) {
      props.combination.isActive = value;
    }

    function validate(): boolean {
        let hasErrors = false;

        tableMultiEntityInput.value!.setError(false);
        preferredMinOccupancyInput.value!.setError(false);
        maximumOccupancyInput.value!.setError(false);
        sortOrderInput.value!.setError(false);

        if (props.combination.tables.length > 10 || props.combination.tables.length < 2) {
            tableMultiEntityInput.value!.setError(true);
            hasErrors = true;
        }

        if (props.combination.preferredMinimumOccupancy == null || props.combination.preferredMinimumOccupancy < 1) {
            preferredMinOccupancyInput.value!.setError(true);
            hasErrors = true;
        }

        if (props.combination.maximumOccupancy == null || props.combination.maximumOccupancy < 1) {
            maximumOccupancyInput.value!.setError(true);
            hasErrors = true;
        }

        if (props.combination.sortOrder != null && props.combination.sortOrder < 0) {
            sortOrderInput.value!.setError(true);
            hasErrors = true;
        }

        return !hasErrors;
    }

    defineExpose({
        validate,
    });
</script>

<style scoped lang="scss">
    .combinations-table-row {
        td {
            padding: 7.5px;
        }
    }
</style>
